import { faInfo, faUser as faUserClosed } from '@fortawesome/pro-duotone-svg-icons';
import { faBars, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppBar, Backdrop, Badge, Box, IconButton, List, MenuItem, styled, Toolbar, Tooltip, Typography } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Species } from "../../api/dtos/dtos";
import { SpeciesTypes } from "../../constants/species/Species";
import AuthContext from "../../contexts/auth/AuthContext";
import { SpeciesContext } from "../../contexts/data/SpeciesContext";
import useGetSpeciesNavContent from "../../hooks/data/useGetSpeciesNavContent";
import NavButton from "../common/buttons/NavButton";
import Dropdown from "../common/Dropdown";

// -- Header logos --
import DeerLogo from '../../assets/images/feral-scan/species/deer/logo.svg';
import FeralCatLogo from '../../assets/images/feral-scan/species/feral-cat/logo.svg';
import FeralFishLogo from '../../assets/images/feral-scan/species/feral-fish/logo.svg';
import FeralGoatLogo from '../../assets/images/feral-scan/species/feral-goat/logo.svg';
import FeralPigLogo from '../../assets/images/feral-scan/species/feral-pig/logo.svg';
import FoxLogo from '../../assets/images/feral-scan/species/fox/logo.svg';
import MouseLogo from '../../assets/images/feral-scan/species/mouse/logo.svg';
import MynaLogo from '../../assets/images/feral-scan/species/myna/logo.svg';
import NewPestsLogo from '../../assets/images/feral-scan/species/new-pests/logo.svg';
import OtherPestsLogo from '../../assets/images/feral-scan/species/other-pests/logo.svg';
import RabbitLogo from '../../assets/images/feral-scan/species/rabbit/logo.svg';
import StarlingLogo from '../../assets/images/feral-scan/species/starling/logo.svg';
import ToadLogo from '../../assets/images/feral-scan/species/toad/logo.svg';
import WildDogLogo from '../../assets/images/feral-scan/species/wild-dog/logo.svg';
import CommonContext from '../../contexts/data/CommonContext';
import MapContext from '../../contexts/data/MapContext';
import UserGroupsContext from '../../contexts/data/UserGroupsContext';
import isCurrentGroupAdmin from '../../functions/user';
import MobileNavButton from '../common/buttons/MobileNavButton';
import NavLogoButton from '../buttons/NavLogoButton';
import HelpModal from '../common/HelpModal';
import MobileNavigationDrawer from '../drawers/MobileNavigationDrawer';
import { AppSettings } from '../../AppSettings';
import ToggleFeedbackButton from '../buttons/SentryFeedbackButton';
import LoginForm from '../forms/LoginForm';
import { NotificationContext } from '../../contexts/data/NotificationContext';

/*
 * -- Components -- 
 */
const StyledAppBar = styled(AppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: theme.palette.common.white
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? undefined : theme.palette.common.white,
    color: theme.palette.mode === 'dark' ? undefined : theme.palette.primary.main,
    minHeight: '75px !important',
    maxHeight: '75px !important',
    width: '100vw',
    margin: '0 auto',

    "@media (min-width: 1300px)": {
        width: '1260px'
    }
}));

// const HeaderLogo = styled(Image)(() => ({
//     height: '60px',

//     '&:hover': {
//         filter: 'brightness(0.7)'
//     }
// }));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    minHeight: '40px', 
    maxHeight: '40px', 
    maxWidth: '40px',
    minWidth: '40px', 
    backgroundColor: theme.palette.primary.main,
    margin: '0 6px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 0px 3px 2px rgba(0,0,0,0.12), 0 0px 2px rgba(0,0,0,0.24)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const SpacingBox = styled(Box)(({ theme }) => ({
    ...theme.mixins.toolbar,
    height: 75
}));

export interface IHeaderProps {}

const Header: FC<IHeaderProps> = () => {
    const location = useLocation();
    const { species: speciesName } = useParams();
    
    const { species: { species} } = useContext(SpeciesContext);
    const { closeDrawer } = useContext(MapContext);
    const { user, openLogin, isLogin, logout } = useContext(AuthContext);
    const { helpOpen, setHelpOpen, mobileNavigationDrawerOpen, setMobileNavigationDrawerOpen } = useContext(CommonContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { filter } = useContext(MapContext);
    const { totalCount } = useContext(NotificationContext);

    const navigate = useNavigate();

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);

    const { data, refresh } = useGetSpeciesNavContent(!species || species === Species.None ? undefined : {
        species: species
    }, {
        revalidateOnFocus: false, 
        revalidateIfStale: false, 
        errorRetryCount: 0
    });

    const navItems = useMemo(() => {
        let items = [];

        if (location.pathname.includes('/management')){
            return [
                <NavButton
                    key={"nav-home"}
                    to={`/${speciesName}`}
                >
                    <Typography variant='body1'>Return to {speciesName?.replace("scan", "")?.replace("alert", "").capitaliseFirstLetter()} Home</Typography>
                </NavButton>
            ];
        }

        if (!data) {
            items = [
                <NavButton
                    key={"nav-home"}
                    to={`/${speciesName}`}
                >
                    <Typography variant='body1'>Home</Typography>
                </NavButton>
            ];

        } else {
            const sorted = data!.sort((a, b) => {
                return a.rank > b.rank ? 
                    1 :
                b.rank > a.rank ?
                    -1 :
                    0
            });
    
            const first = sorted[0];
    
            items = sorted.map(x => {
                return (
                    <NavButton
                        isDefault={x.id === first.id ? speciesName : undefined}
                        key={`nav-${x.rank}-${x.id}}`}
                        to={x.link}
                        menuItems={x.items}
                    >
                        <Typography variant='body1'>{x.name}</Typography>
                    </NavButton>
                );
            });
        }

        if (!isGroupAdmin) {
            items.push(
                <NavButton
                    key={`nav-start-mapping}`}
                    to={`/${speciesName}/map`}
                >
                    <Box width='24px' height='24px' marginRight='5px'>
                        <svg version="1.2" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="-10 0 1000 960" strokeWidth="40">
                        <path d="M758.57,897.774c11.102,7.5,26.201,1.7,29.5-11.2l16.5-65c1.301-5.1-2.5-10-7.799-10h-111c-6.701,0-10.4,7.7-6.301,12.9
                            C707.371,859.874,738.172,883.974,758.57,897.774z"/>
                        <path d="M34.971,509.374c28.3,56.4,54.6,117.7,45.2,177.5c-0.8,5.301,3.3,10.101,8.7,10.101h39c2.8,0,5.4-1.3,7-3.5
                            c47.3-64,111.3-84,111.3-84c57.8-17.4,120.1-32.8,160.1-32.8c74.6,0,92.401,61.199,96.401,84.6c0.898,5.2,5.398,9,10.699,9
                            c4.801,0,9.4-2,12.6-5.6l34.701-38.4c3.5-3.8,9.799-1.9,10.6,3.1c10.699,67.101,63.5,136.301,73.5,141.7c0.9,0.5,1.9,0.7,2.9,0.7
                            h155.1c3.199,0,5.799-2.4,6.1-5.5c5.5-58.8,95.699-143.7,130.199-174.3c8.5-7.5,13.301-18.2,13.301-29.5v-74.101
                            c0-1-0.199-1.899-0.6-2.8l-88-176c-0.6-1.3-1.701-2.3-3-2.9c-72.1-32.9-68.201-115-66.801-129.9c0.1-1.5-0.299-3.1-1.199-4.3
                            l-21.201-27.6c-2-2.6-5.799-3.2-8.5-1.2l-9.699,7c0-42.6-17.699-79.1-27.6-96.3c-2.6-4.5-9.201-4-11.1,0.8l-20.201,52.2
                            c-0.299,0.7-0.4,1.5-0.4,2.2c-0.1,43.3-7.299,72.4-15,91.1c-6.898,16.8-27.398,23-42.6,13.1c-29.9-19.5-78.4-51.2-95-62
                            c-3.1-2-3.699-6.2-1.4-9.1l46.701-56.7c3.299-4,0.4-10.1-4.801-10.1h-145.4c-1.6,0-3.1,0.6-4.2,1.7l-47.699,45
                            c-2.5,2.4-2.601,6.4-0.101,8.8l12.601,12.7c-40.601,13.5-67.9-13.9-77.7-26.2c-2.3-2.8-6.5-3.1-9.101-0.6l-104.3,100.5
                            c-0.9,0.9-1.6,2-1.8,3.3c-11,63.5-133.3,106.7-171.6,118.7c-7.1,2.2-12.5,8-14.2,15.2l-17.9,74.6c-1.1,4.7-0.6,9.601,1.4,13.9
                            C11.771,464.374,23.471,486.474,34.971,509.374z"/>
                        </svg>
                    </Box>
                    <Typography variant='body1'>Start Mapping</Typography>
                </NavButton>
            );
        }

        return items;

    }, [data, location, isGroupAdmin]);

    const mobileNavItems = useMemo(() => {
        if (!speciesName) return [];

        if (location.pathname.includes('/management')) {
            return [
                <MobileNavButton
                    key="nav-home"
                    to={`/${speciesName}`}
                >
                    <Typography variant="body1">
                        Return to {speciesName
                            ?.replace("scan", "")
                            ?.replace("alert", "")
                            ?.capitaliseFirstLetter()}
                    </Typography>
                </MobileNavButton>
            ];
        }

        const items: React.ReactNode[] = [];

        if (!data || data.length === 0) {
            items.push(
                <MobileNavButton
                    key="nav-home"
                    to={`/${speciesName}`}
                >
                    <Typography variant="body1">Home</Typography>
                </MobileNavButton>
            );
        } else {
            const sorted = [...data].sort((a, b) => a.rank - b.rank);
            const defaultItem = sorted[0];

            sorted.forEach(x => {
                items.push(
                    <MobileNavButton
                        key={`nav-${x.id}`}
                        isDefault={x.id === defaultItem.id ? speciesName : undefined}
                        to={x.link}
                        menuItems={x.items}
                    >
                        <Typography variant="body1">{x.name}</Typography>
                    </MobileNavButton>
                );
            });
        }

        if (!isGroupAdmin) {
            items.push(
                <MobileNavButton
                    key="nav-start-mapping"
                    to={`/${speciesName}/map`}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            width={24}
                            height={24}
                            marginRight="8px"
                            display="flex"
                            alignItems="center"
                        >
                            <svg
                                viewBox="-10 0 1000 960"
                                strokeWidth="40"
                            >
                                <path d="M758.57,897.774c11.102,7.5,26.201,1.7,29.5-11.2l16.5-65c1.301-5.1-2.5-10-7.799-10h-111c-6.701,0-10.4,7.7-6.301,12.9
                                    C707.371,859.874,738.172,883.974,758.57,897.774z"/>
                                <path d="M34.971,509.374c28.3,56.4,54.6,117.7,45.2,177.5c-0.8,5.301,3.3,10.101,8.7,10.101h39c2.8,0,5.4-1.3,7-3.5
                                    c47.3-64,111.3-84,111.3-84c57.8-17.4,120.1-32.8,160.1-32.8c74.6,0,92.401,61.199,96.401,84.6c0.898,5.2,5.398,9,10.699,9
                                    c4.801,0,9.4-2,12.6-5.6l34.701-38.4c3.5-3.8,9.799-1.9,10.6,3.1c10.699,67.101,63.5,136.301,73.5,141.7c0.9,0.5,1.9,0.7,2.9,0.7
                                    h155.1c3.199,0,5.799-2.4,6.1-5.5c5.5-58.8,95.699-143.7,130.199-174.3c8.5-7.5,13.301-18.2,13.301-29.5v-74.101
                                    c0-1-0.199-1.899-0.6-2.8l-88-176c-0.6-1.3-1.701-2.3-3-2.9c-72.1-32.9-68.201-115-66.801-129.9c0.1-1.5-0.299-3.1-1.199-4.3
                                    l-21.201-27.6c-2-2.6-5.799-3.2-8.5-1.2l-9.699,7c0-42.6-17.699-79.1-27.6-96.3c-2.6-4.5-9.201-4-11.1,0.8l-20.201,52.2
                                    c-0.299,0.7-0.4,1.5-0.4,2.2c-0.1,43.3-7.299,72.4-15,91.1c-6.898,16.8-27.398,23-42.6,13.1c-29.9-19.5-78.4-51.2-95-62
                                    c-3.1-2-3.699-6.2-1.4-9.1l46.701-56.7c3.299-4,0.4-10.1-4.801-10.1h-145.4c-1.6,0-3.1,0.6-4.2,1.7l-47.699,45
                                    c-2.5,2.4-2.601,6.4-0.101,8.8l12.601,12.7c-40.601,13.5-67.9-13.9-77.7-26.2c-2.3-2.8-6.5-3.1-9.101-0.6l-104.3,100.5
                                    c-0.9,0.9-1.6,2-1.8,3.3c-11,63.5-133.3,106.7-171.6,118.7c-7.1,2.2-12.5,8-14.2,15.2l-17.9,74.6c-1.1,4.7-0.6,9.601,1.4,13.9
                                    C11.771,464.374,23.471,486.474,34.971,509.374z"/>
                            </svg>
                        </Box>
                        <Typography variant="body1">
                            Start Mapping
                        </Typography>
                    </Box>
                </MobileNavButton>
            );
        }

        return items;
    }, [data, location.pathname, speciesName, isGroupAdmin]);

    const handleLogout = useCallback(async () => {
        await logout();
        navigate(`/${speciesName}`);
    }, [navigate, logout]);

    const [_logo, setLogo] = useState<string | null>(null);

    const [profileOpen, setProfileOpen] = useState<boolean>(false);
    const profileAnchor = useRef<HTMLButtonElement>(null);

    const [helpTooltipOpen, setHelpTooltipOpen] = useState<boolean>(false);
    const [accountTooltipOpen, setAccountTooltipOpen] = useState<boolean>(false);

    useEffect(() => {
        switch (species) {
            case SpeciesTypes.WILDDOG.toSpecies(): setLogo(WildDogLogo); break;
            case SpeciesTypes.DEER.toSpecies(): setLogo(DeerLogo); break;
            case SpeciesTypes.RABBIT.toSpecies(): setLogo(RabbitLogo); break;
            case SpeciesTypes.FERALCAT.toSpecies(): setLogo(FeralCatLogo); break;
            case SpeciesTypes.FOX.toSpecies(): setLogo(FoxLogo); break;
            case SpeciesTypes.FERALPIG.toSpecies(): setLogo(FeralPigLogo); break;
            case SpeciesTypes.MYNA.toSpecies(): setLogo(MynaLogo); break;
            case SpeciesTypes.TOAD.toSpecies(): setLogo(ToadLogo); break;
            case SpeciesTypes.MOUSE.toSpecies(): setLogo(MouseLogo); break;
            case SpeciesTypes.FERALGOAT.toSpecies(): setLogo(FeralGoatLogo); break;
            case SpeciesTypes.FERALFISH.toSpecies(): setLogo(FeralFishLogo); break;
            case SpeciesTypes.STARLING.toSpecies(): setLogo(StarlingLogo); break;
            case SpeciesTypes.OTHERPESTS.toSpecies(): setLogo(OtherPestsLogo); break;
            case SpeciesTypes.NEWPESTS.toSpecies(): setLogo(NewPestsLogo); break;
            default: setLogo(WildDogLogo);
        }
    });

    useEffect(() => {
        refresh();
    }, [user]);

    return (
        <>
            <StyledAppBar>
                <StyledToolbar>

                    {/* -- Nav Logo -- */}
                    <NavLogoButton />

                    {/* -- Nav Menu -- */}
                    <Box 
                        sx={{
                            marginTop: '20px',
                            display: 'flex',
                            flexDirection: 'row',

                            '@media (max-width: 1300px)': {
                                display: 'none'
                            }
                        }}
                    >
                        <List sx={{ display: 'flex', flexDirection: 'row', textDecoration: 'none !important' }}>
                            {navItems}
                        </List>
                    </Box>

                    {/* -- Profile and Info -- */}
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Box
                            onMouseOver={() => setHelpTooltipOpen(true)}
                            onMouseLeave={() => setHelpTooltipOpen(false)}
                        >
                            <Tooltip
                                arrow
                                placement='bottom'
                                open={helpTooltipOpen}
                                title={
                                    <Typography>Instructions</Typography>
                                }
                            >
                                <StyledIconButton 
                                    disableRipple
                                    onClick={() => setHelpOpen(!helpOpen)}
                                >
                                    <FontAwesomeIcon color='white' fontSize='1.3rem' icon={faInfo} />
                                </StyledIconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            onMouseOver={() => !profileOpen ? setAccountTooltipOpen(true) : setAccountTooltipOpen(false)}
                            onMouseLeave={() => setAccountTooltipOpen(false)}
                        >
                            <Tooltip
                                arrow
                                placement='bottom'
                                open={accountTooltipOpen}
                                title={
                                    <Typography>{user ? "Logout" : "Login"}</Typography>
                                }
                            >
                                <StyledIconButton 
                                    disableRipple 
                                    ref={profileAnchor}
                                    onClick={() => {setAccountTooltipOpen(false);setProfileOpen(c => !c)}}
                                >
                                    <Badge
                                        color="error"
                                        badgeContent={totalCount}
                                        invisible={!user || totalCount === undefined || totalCount === 0}
                                        max={99}
                                        sx={{
                                            "& .MuiBadge-badge": {
                                                top: -5,
                                                right: -8,
                                            },
                                        }}
                                    >
                                        {
                                            user ?
                                                <FontAwesomeIcon
                                                    color="white"
                                                    fontSize="1.2rem"
                                                    icon={faUserClosed}
                                                />
                                                :
                                                <FontAwesomeIcon
                                                    color="white"
                                                    fontSize="1.2rem"
                                                    icon={faUser}
                                                />
                                        }
                                    </Badge>
                                    <Dropdown
                                        anchor={profileAnchor.current}
                                        open={profileOpen}
                                    >
                                        {/* TODO: Fix the style of this button so its "&:active" when dropdown is active */}
                                        {
                                            !user ?
                                                <>
                                                    <MenuItem onClick={() => {openLogin(); closeDrawer();}}>Log In</MenuItem>
                                                    <MenuItem onClick={() => navigate(`/${speciesName}/register`)}>Register</MenuItem>
                                                </> :
                                                <>
                                                    <MenuItem onClick={() => navigate(`/${speciesName}/profile`)}>Profile</MenuItem>
                                                    <MenuItem onClick={() => navigate(`/${speciesName}/notifications`)}>Notifications</MenuItem>
                                                    <MenuItem onClick={handleLogout} >Log Out</MenuItem>
                                                </>
                                        }
                                        
                                    </Dropdown>
                                </StyledIconButton>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                display: 'block',
                                '@media (min-width: 1300px)': {
                                    display: 'none'
                                }
                            }}
                        >
                            <StyledIconButton 
                                disableRipple
                                onClick={() => setMobileNavigationDrawerOpen(!mobileNavigationDrawerOpen)}
                            >
                                <FontAwesomeIcon color='white' fontSize='1.3rem' icon={faBars} />
                            </StyledIconButton>
                        </Box>
                    </Box>
                    {
                        AppSettings.sentry.dsn && 
                        AppSettings.sentry.enableReportABug && (
                            <ToggleFeedbackButton />
                        )
                    }
                </StyledToolbar>
            </StyledAppBar>
            <SpacingBox />
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 100 }}
                open={isLogin}
            >
                <LoginForm />
            </Backdrop>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 100 }}
                open={helpOpen}
            >
                <HelpModal />
            </Backdrop>
            <Box
                sx={{
                    display: 'block',
                    '@media (min-width: 1300px)': {
                        display: 'none'
                    }
                }}
            >
                <MobileNavigationDrawer navItems={mobileNavItems} />
            </Box>
        </>
    )
}

export default Header;